<template>
  <div v-if="!validationStatusEmptyOrValid">
    <div v-if="validationStatus.errors">
      <div
        class="
          sf-below-3
          sf-layer-rounded
          sf-bg-danger-subtle-2
          sf-c-danger-light-1
          sf-pad-2
          sf-level-vertical
        "
      >
        <div v-if="!validationStatus.errors.invalidFile" class="sf-level-item">
          {{ I18n.t('budget_set_import.error_modal.whoops') }}
        </div>
        <div v-if="validationStatus.errors.empty" class="sf-level-item">
          {{ I18n.t('budget_set_import.error_modal.empty') }}
          <br />
          {{ I18n.t('budget_set_import.error_modal.empty_tip') }}
        </div>
        <div v-if="validationStatus.errors.extraAccounts" class="sf-level-item">
          {{ I18n.t('budget_set_import.error_modal.extra_accounts') }}
          {{ validationStatus.errors.extraAccounts.slice(0, 5).join(', ') }}
          <span v-if="validationStatus.errors.extraAccounts.length > 5">
            {{
              usedXTimesMoreValidationText(
                validationStatus.errors.extraAccounts
              )
            }}.
          </span>
          <span v-else> . </span>
          {{ I18n.t('budget_set_import.error_modal.extra_accounts_tip') }}
        </div>
        <div
          v-if="validationStatus.errors.duplicatedAccounts"
          class="sf-level-item"
        >
          {{ I18n.t('budget_set_import.error_modal.duplicated_accounts') }}
          {{
            validationStatus.errors.duplicatedAccounts.slice(0, 5).join(', ')
          }}
          <span v-if="validationStatus.errors.duplicatedAccounts.length > 5">
            {{
              usedXTimesMoreValidationText(
                validationStatus.errors.duplicatedAccounts
              )
            }}.
          </span>
          <span v-else> . </span>
          {{ I18n.t('budget_set_import.error_modal.duplicated_accounts_tip') }}
        </div>
        <div v-if="validationStatus.errors.extraPeriods" class="sf-level-item">
          {{ I18n.t('budget_set_import.error_modal.extra_periods') }}
          {{ validationStatus.errors.extraPeriods.slice(0, 5).join(', ') }}
          <span v-if="validationStatus.errors.extraPeriods.length > 5">
            {{
              usedXTimesMoreValidationText(
                validationStatus.errors.extraPeriods
              )
            }}.
          </span>
          <span v-else> . </span>
          {{ I18n.t('budget_set_import.error_modal.extra_periods_tip') }}
        </div>
        <div v-if="validationStatus.errors.invalidValues" class="sf-level-item">
          {{ I18n.t('budget_set_import.error_modal.invalid_values') }}
          {{ validationStatus.errors.invalidValues.slice(0, 5).join(', ') }}
          <span v-if="validationStatus.errors.invalidValues.length > 5">
            {{
              usedXTimesMoreValidationText(
                validationStatus.errors.invalidValues
              )
            }}.
          </span>
          <span v-else> . </span>
          {{ I18n.t('budget_set_import.error_modal.invalid_values_tip') }}
        </div>
        <div
          v-if="validationStatus.errors.validationErrors"
          class="sf-level-item"
        >
          {{ I18n.t('budget_set_import.error_modal.validation_errors') }}
          {{ validationStatus.errors.validationErrors.slice(0, 5).join(', ') }}
          <span v-if="validationStatus.errors.validationErrors.length > 5">
            {{
              usedXTimesMoreValidationText(
                validationStatus.errors.validationErrors
              )
            }}.
          </span>
          <span v-else> . </span>
          {{ I18n.t('budget_set_import.error_modal.validation_errors_tip') }}
        </div>
        <div v-if="validationStatus.errors.invalidFile" class="sf-level-item">
          {{ I18n.t('budget_set_import.error_modal.invalid_file_type') }}
        </div>
      </div>
      <div class="sf-level sf-level-center">
        <SfButtonDs variant="secondary" @click="handleClose">
          {{ I18n.t('budget_set_import.warning_modal.cancel') }}
        </SfButtonDs>
      </div>
    </div>
    <div v-else-if="validationStatus.overrideValueWarning">
      <div
        class="
          sf-below-3
          sf-layer-rounded
          sf-bg-warning-subtle-2
          sf-c-warning-light-2
          sf-pad-2
        "
      >
        {{ warningModalMessage }}
      </div>
      <div class="sf-level sf-level-center">
        <a class="sf-btn" @click="handleClose">
          {{ I18n.t('budget_set_import.warning_modal.cancel') }} </a
        ><span class="sf-btn sf-btn-warning" @click="handleOverwriteConfirm">
          {{ I18n.t('budget_set_import.warning_modal.ok') }}
        </span>
      </div>
    </div>
    <div
      v-else
      class="
        sf-below-3
        sf-layer-rounded
        sf-bg-danger-subtle-2
        sf-c-danger-light-1
        sf-pad-2
        sf-level-vertical
      "
    >
      <div
        v-dompurify-html="
          I18n.t('budget_set_import.unexpected_error_modal.message')
        "
        class="sf-level-item"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { BudgetImportValidationStatus } from '../types'

import { computed } from 'vue'

import { SfButtonDs } from '@silverfin/ui-components'

interface Props {
  budgetSetName: string
  validationStatus: BudgetImportValidationStatus
}

const props = defineProps<Props>()

const emit =
  defineEmits<{
    (_e: 'close'): void
    (_e: 'confirm'): void
  }>()

const I18n = window.I18n

const warningModalMessage = computed(() => {
  return window.I18n.t('budget_set_import.warning_modal.message', {
    budget_set_name: props.budgetSetName,
  })
})

const validationStatusEmptyOrValid = computed(() => {
  return (
    Object.entries(props.validationStatus).length === 0 ||
    props.validationStatus.valid
  )
})

function usedXTimesMoreValidationText(errorsArray: Array<string>): string {
  return window.I18n.t('shared.used_x_times_more', {
    count: errorsArray.length - 5,
  })
}

function handleClose(): void {
  emit('close')
}

function handleOverwriteConfirm(): void {
  emit('confirm')
}
</script>
